import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import htmr from 'htmr';
import { IUpdateFormData } from '../account-settings-models/interface';
import accountApi from '../../../api/account';
import { useQuery } from 'react-query';
import { objectToFormData } from '../../../utils/helpers';

const useProfile = () => {
  const [apiError, setApiError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [uploadImage, setUploadImage] = useState<string | undefined>();
  const [uploadFile, setUploadFile] = useState<File>();
  const [userId] = useState(localStorage.getItem('c-uid') || '');

  /**get user profile api call */
  const { data, refetch } = useQuery(['getProfle', userId], () =>
    accountApi.getProfile(+userId)
  );

  /**formik validationschema setup */
  const validationSchema = Yup.object({
    name: Yup.string().required('Firstname is required'),
    email: Yup.string().required('Email is required'),
    phoneNumber: Yup.string(),
    id: Yup.string(),
  });

  /**upload image click function */
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  /**upload image onChange function */
  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileUploaded = event.target.files?.[0];
    setUploadFile(fileUploaded);
    if (fileUploaded) {
      if (fileUploaded?.size > 2000000) {
        toast.error(
          `You cannot select a file greater than 2MB",${fileUploaded?.name} is greater than 2MB`
        );
      } else {
        const reader: FileReader = new FileReader();
        reader.readAsDataURL(fileUploaded);
        reader.onload = () => {
          setUploadImage(reader?.result as string);
        };
      }
    }
  };

  /**handle form value submission function */
  const handleSubmit = async (values: IUpdateFormData) => {
    const Surname = values?.name?.split(' ')[0];
    const FirstName = values?.name?.split(' ')[1];
    const MiddleName = values?.name?.split(' ')[2];

    try {
      handleApiError('');
      setIsLoading(true);
      const res = await accountApi.updateProfile(
        objectToFormData({
          Id: data?.data?.id,
          FirstName,
          MiddleName,
          Surname,
          PhoneNumber: values.phoneNumber,
          ProfilePix: uploadFile,
        })
      );
      if (res.success) {
        toast.success('Account Updated successfully.');
        formik.resetForm();
        refetch();
      } else {
        handleApiError(htmr(res.description) as string);
      }
    } catch (e) {
      handleApiError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  /**initialize form using formik */
  const formik = useFormik<IUpdateFormData>({
    initialValues: {
      name:
        `${data?.data?.lastName} ${data?.data?.firstName} ${data?.data?.middleName}` ||
        '',
      email: data?.data?.email || '',
      phoneNumber: data?.data?.phoneNumber || '',
      id: data?.data?.id || '',
    },
    onSubmit: handleSubmit,
    validateOnBlur: true,
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema,
  });

  /** api error function */
  const handleApiError = (error: string) => {
    setApiError(error);
  };

  const handleCloseAlert = () => {
    setApiError('');
  };

  /**check if image is valid function */
  const IsValidImage = (url: string) => {
    const img = new Image();
    img.src = url;
    img.onload = () => setUploadImage(data?.data?.profilePixUrl);
    img.onerror = () => setUploadImage('');
  };

  /**check if image is valid when component loads */
  useEffect(() => {
    IsValidImage(data?.data?.profilePixUrl);
  }, [data]);

  return {
    formik,
    apiError,
    isLoading,
    uploadImage,
    data,
    fileInputRef,
    handleCloseAlert,
    handleUploadClick,
    onFileChange,
  };
};

export default useProfile;
